/** Close round… (screens §F1 state B): what goes in for each unsigned human team; rescue only where `rescuable` (OD4-7). */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BoardTeamRow, RoundSummary } from "@bsim/contracts";
import { Dialog, DialogCancel } from "../theme/Dialog";
import { fallbackWords, humanRows } from "./derive";
import type { Act } from "./useAct";

export function CloseDialog({ cohortId, round, teams, surpriseHeld, act, onClose }: {
  readonly cohortId: string; readonly round: RoundSummary; readonly teams: readonly BoardTeamRow[]; readonly surpriseHeld: boolean; readonly act: Act; readonly onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // The count of signed teams is read when the dialog opens as well as while it is up, so it never shows a snapshot.
  useEffect(() => { void queryClient.invalidateQueries({ queryKey: ["cohortBoard"] }); }, [queryClient]);
  const humans = humanRows(teams);
  const unsigned = humans.filter((t) => t.closeFallback !== "signed");
  const [rescue, setRescue] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const toggle = (id: string) => setRescue((prev) => { const next = new Set(prev); if (!next.delete(id)) next.add(id); return next; });

  const confirm = async () => {
    setBusy(true);
    const res = await act("roundLock", { cohortId, roundId: round.id }, { expectedStateVersion: round.stateVersion, force: unsigned.length > 0, rescueTeamIds: [...rescue] });
    setBusy(false);
    if (res.ok) onClose();
  };

  return (
    <Dialog
      title={`Close Round ${round.roundNo}?`} testId="dialog.close" onClose={onClose}
      lead={`Teams can no longer edit. ${surpriseHeld ? "The held surprise can be released once the year has run. " : ""}${humans.length - unsigned.length} of ${humans.length} teams have signed.`}
      footer={<>
        <span className="gx-small gx-faint gx-grow">A rescued draft is flagged on the board, in the debrief and in the team's evidence. Unanswered planned events take their card defaults.</span>
        <DialogCancel onClose={onClose} />
        <button type="button" className="gx-btn gx-btn--primary" disabled={busy} data-testid="dialog.close.confirm" onClick={() => void confirm()}>Close round</button>
      </>}
    >
      {unsigned.map((t) => {
        const fb = fallbackWords(t, round.roundNo, true);
        return (
          <div key={t.teamId} className="gx-fac-dr" data-testid={`close.team.${t.teamId}`}>
            <b>{t.name}</b>
            <span>{fb.lead} <b>{fb.strong}</b> {fb.tail}</span>
            {t.rescuable ? (
              <label className="gx-fac-chk">
                <input type="checkbox" checked={rescue.has(t.teamId)} onChange={() => toggle(t.teamId)} aria-label={`Submit ${t.name}'s current draft as unattested`} />
                Submit current draft{t.draftVersion === null ? "" : ` v${t.draftVersion}`} as unattested
              </label>
            ) : <span className="gx-small gx-faint">Nothing to rescue</span>}
          </div>
        );
      })}
    </Dialog>
  );
}
